import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import PageLoader from "../components/common/PageLoader";
import PersonCell from "../components/common/PersonCell";
import StatusBadge from "../components/common/StatusBadge";
import Table from "../components/common/Table";
import { agentApi } from "../lib/apiClient";
import { showApiError, showSuccessAction } from "../lib/alerts";
import { asArray, downloadBlob, formatDate, unwrapData, appendUniqueById, readPagination } from "../lib/catalog";
import { isDownloadEnabled, releaseLabel } from "../lib/documentRelease";

const PAGE_SIZE = 20;

export default function VisasPage() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      try {
        const payload = await agentApi.get("/travel-partner/reports/visa", {
          params: { page: 1, limit: PAGE_SIZE },
        });
        if (!alive) return;
        const items = asArray(unwrapData(payload));
        const pagination = readPagination(payload);
        setRows(items);
        setPage(1);
        setHasNext(pagination.hasNext);
        setTotal(pagination.total || items.length);
      } catch (fetchError) {
        if (alive) {
          setRows([]);
          setHasNext(false);
          showApiError(fetchError, "LOAD_VISAS");
        }
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  async function loadMore() {
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const payload = await agentApi.get("/travel-partner/reports/visa", {
        params: { page: nextPage, limit: PAGE_SIZE },
      });
      const items = asArray(unwrapData(payload));
      const pagination = readPagination(payload);
      setRows((current) => appendUniqueById(current, items, (row) => row.visa_id || `${row.booking_id}-${row.passenger_id}`));
      setPage(nextPage);
      setHasNext(pagination.hasNext);
      setTotal(pagination.total || 0);
    } catch (fetchError) {
      showApiError(fetchError, "LOAD_VISAS");
    } finally {
      setLoadingMore(false);
    }
  }

  async function downloadVisa(visaId, fileName) {
    try {
      const blob = await agentApi.get(`/travel-partner/visas/${visaId}/download`, { responseType: "blob" });
      downloadBlob(blob, fileName || `visa-${visaId}`);
      showSuccessAction("DOWNLOAD_VISA");
    } catch (downloadError) {
      showApiError(downloadError, "DOWNLOAD_VISA");
    }
  }

  const filtered = rows.filter((row) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return [row.booking_reference, row.passenger_name, row.visa_status, row.visa_number]
      .some((value) => String(value || "").toLowerCase().includes(query));
  });

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Visas"
          title="Visa tracking for your bookings"
          description="Issued visa files stay locked until the booking payment is complete. Passport and photo uploads remain available from Documents."
          items={[DASHBOARD_CRUMB, { label: "Services" }, { label: "Visas" }]}
          actions={
            <input
              type="search"
              className="form-control page-search"
              placeholder="Search loaded visas"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          }
        />

        {loading ? <PageLoader message="Loading visas..." /> : null}

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <Table>
              <thead>
                <tr>
                  <th>Booking</th>
                  <th>Pilgrim</th>
                  <th>Status</th>
                  <th>Visa number</th>
                  <th>Issued</th>
                  <th>Release</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? filtered.map((row) => (
                  <tr key={row.visa_id || `${row.booking_id}-${row.passenger_id}`}>
                    <td>
                      <Link to={`/visas/${row.booking_id}`} className="agent-table-link">
                        {row.booking_reference}
                      </Link>
                    </td>
                    <td><PersonCell name={row.passenger_name} /></td>
                    <td><StatusBadge value={row.visa_status || "-"} /></td>
                    <td>{row.visa_number || "-"}</td>
                    <td>{formatDate(row.issued_at)}</td>
                    <td>
                      <StatusBadge value={row.release_state}>{releaseLabel(row.release_state)}</StatusBadge>
                    </td>
                    <td className="text-end">
                      <div className="d-inline-flex align-items-center justify-content-end gap-2">
                        <Link
                          to={`/visas/${row.booking_id}`}
                          className="agent-icon-btn"
                          title="View details"
                          aria-label="View details"
                        >
                          <iconify-icon icon="solar:eye-outline"></iconify-icon>
                        </Link>
                        {row.visa_id && row.visa_file_name ? (
                          <button
                            type="button"
                            className="agent-icon-btn"
                            title={isDownloadEnabled(row) ? "Download" : "Locked"}
                            aria-label={isDownloadEnabled(row) ? "Download" : "Locked"}
                            disabled={!isDownloadEnabled(row)}
                            onClick={() => downloadVisa(row.visa_id, row.visa_file_name)}
                          >
                            <iconify-icon icon="solar:download-minimalistic-outline"></iconify-icon>
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="7" className="text-secondary text-center py-4">{loading ? "Loading..." : "No visa records yet. Visas are tracked against package bookings."}</td></tr>
                )}
              </tbody>
            </Table>
            {!loading && hasNext ? (
              <div className="text-center mt-3">
                <button type="button" className="btn btn-outline-primary" disabled={loadingMore} onClick={loadMore}>
                  {loadingMore ? "Loading..." : `Load more visas${total ? ` (${rows.length}/${total})` : ""}`}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </AgentLayout>
  );
}
